import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { X, ArrowRight } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";

// Map the current route to a popup page key (matches backend POPUP_PAGE_KEYS)
function pageKeyFor(pathname) {
  const p = (pathname || "/").toLowerCase();
  if (p === "/") return "home";
  if (p.startsWith("/php2shell-security-advisory") || p.startsWith("/security-advisory")) return "advisory";
  if (p.startsWith("/hostinger-offer")) return "hostinger-offer";
  if (p.startsWith("/services")) return "services";
  if (p.startsWith("/products")) return "products";
  if (p.startsWith("/solutions")) return "solutions";
  if (p.startsWith("/events")) return "events";
  if (p.startsWith("/resources")) return "resources";
  if (p.startsWith("/blog")) return "blog";
  if (p.startsWith("/contact")) return "contact";
  return null;
}

const sessionKey = (id) => `popup-shown-${id}`;

const postEvent = (id, event) => {
  api.post(`/popups/${id}/event`, { event }).catch(() => { /* analytics are non-critical */ });
};

function PopupCard({ popup, onClose }) {
  const navigate = useNavigate();
  const [ctaVisible, setCtaVisible] = useState(popup.media_type === "image");
  const src = popup.media_url.startsWith("http") ? popup.media_url : mediaUrl(popup.media_url);
  const poster = popup.poster_url ? (popup.poster_url.startsWith("http") ? popup.poster_url : mediaUrl(popup.poster_url)) : undefined;

  const close = (reason) => {
    postEvent(popup.id, "close");
    trackEvent("popup_close", { popup: popup.title, reason });
    onClose();
  };
  const ctaClick = () => {
    postEvent(popup.id, "cta_click");
    trackEvent("popup_cta_click", { popup: popup.title, target: popup.cta_url });
    sessionStorage.setItem(sessionKey(popup.id), "1");
    if (/^https?:\/\//i.test(popup.cta_url)) {
      window.location.href = popup.cta_url;
    } else {
      onClose();
      navigate(popup.cta_url);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-ink/80 backdrop-blur-sm"
      data-testid="popup-modal" onClick={() => close("backdrop")}>
      <div className="relative w-full max-w-2xl bg-ink rounded-2xl overflow-hidden shadow-2xl border border-white/10"
        onClick={(e) => e.stopPropagation()}>
        <button onClick={() => close("x")} aria-label="Close popup" data-testid="popup-close"
          className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-black/60 hover:bg-red-600 text-white flex items-center justify-center transition-colors">
          <X size={18} />
        </button>

        {popup.media_type === "video" ? (
          <video src={src} poster={poster} autoPlay muted controls playsInline
            onEnded={() => { setCtaVisible(true); postEvent(popup.id, "finish"); trackEvent("popup_video_finished", { popup: popup.title }); }}
            className="w-full max-h-[65vh] bg-black object-contain" data-testid="popup-video" />
        ) : (
          <img src={src} alt={popup.title} className="w-full max-h-[65vh] object-contain bg-black" data-testid="popup-image" />
        )}

        <div className="p-5 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-white/80 font-medium text-center sm:text-left">{popup.title}</p>
          {popup.cta_label && (
            ctaVisible ? (
              <button onClick={ctaClick} data-testid="popup-cta"
                className="inline-flex items-center gap-2 bg-cobalt hover:bg-cobalt-dark text-white text-sm font-bold rounded-full px-6 py-3 transition-colors shrink-0">
                {popup.cta_label} <ArrowRight size={15} />
              </button>
            ) : (
              <p className="text-xs text-white/50 italic" data-testid="popup-cta-pending">Your next step appears when the video finishes…</p>
            )
          )}
        </div>
      </div>
    </div>
  );
}

export default function PopupManager() {
  const { pathname } = useLocation();
  const [popup, setPopup] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const key = pageKeyFor(pathname);
    setPopup(null);
    setVisible(false);
    if (!key) return;
    let cancelled = false;
    let timer = null;
    (async () => {
      try {
        const { data } = await api.get("/popups/active", { params: { page: key } });
        const candidate = (data || []).find((p) => !sessionStorage.getItem(sessionKey(p.id)));
        if (!cancelled && candidate) {
          timer = setTimeout(() => {
            if (!cancelled) {
              setPopup(candidate);
              setVisible(true);
              postEvent(candidate.id, "view");
              trackEvent("popup_view", { popup: candidate.title, page: key });
            }
          }, Math.max(0, (candidate.delay_seconds || 0) * 1000));
        }
      } catch { /* popups are non-critical */ }
    })();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [pathname]);

  useEffect(() => {
    if (!visible) return;
    const onKey = (e) => { if (e.key === "Escape") { sessionStorage.setItem(sessionKey(popup.id), "1"); setVisible(false); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [visible, popup]);

  if (!visible || !popup) return null;
  return (
    <PopupCard popup={popup} onClose={() => { sessionStorage.setItem(sessionKey(popup.id), "1"); setVisible(false); }} />
  );
}
